import { Button } from '@nextui-org/react'
import * as Sentry from '@sentry/react'
import { Component, ErrorInfo, ReactNode } from 'react'
import LoginBackgroundImage from '../../assets/image/LoginBackground.png'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo)
    }

    // Store errorInfo for stack trace display
    this.setState({ errorInfo })

    // Send to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    })
  }

  render() {
    if (this.state.hasError) {
      console.info('NODE_ENV: ', process.env.NODE_ENV)
      return (
        <div className="relative w-full h-[100dvh]">
          <img
            className="absolute w-full h-[100dvh] object-cover z-0"
            src={LoginBackgroundImage}
            alt=""
          />
          <div className="absolute w-full h-[100dvh] flex flex-col justify-center items-center px-4">
            {/* Desktop Layout */}
            <div className="hidden md:flex w-[400px] flex-col justify-center items-center -mt-[45px]">
              <div className="flex flex-col items-center">
                <p className="h-[140px] text-[8rem] font-bold text-white align-top">ERROR</p>
                <p className="text-[1.875rem] h-fit font-bold p-0 text-white mt-[45px]">
                  SOMETHING WENT WRONG
                </p>
              </div>
              <div className="w-[350px] mt-10">
                <p className="text-[14px] text-white text-center">
                  We're sorry, but something unexpected happened. Please try refreshing the page.
                </p>
              </div>
              <div className="w-[295px] grid grid-cols-2 gap-4 mt-10">
                <Button
                  className="w-full text-white font-bold text-[1rem] bg-tdplplum"
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </Button>
                <Button
                  className="text-white font-bold text-[1rem] bg-tdpllightgrey"
                  onClick={() => (window.location.href = window.location.origin)}
                >
                  Home Page
                </Button>
              </div>

              {/* Show error stack trace in development */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-10 w-[400px]">
                  <summary className="text-sm text-white cursor-pointer hover:text-gray-300">
                    Show error stack trace
                  </summary>
                  <div className="mt-2 p-4 bg-black/50 rounded text-xs text-white overflow-auto max-h-[200px]">
                    <div className="font-bold mb-2">Error:</div>
                    <pre className="whitespace-pre-wrap break-all">
                      {this.state.error.stack || this.state.error.toString()}
                    </pre>
                    {this.state.errorInfo && (
                      <>
                        <div className="font-bold mt-4 mb-2">Component Stack:</div>
                        <pre className="whitespace-pre-wrap break-all">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </>
                    )}
                  </div>
                </details>
              )}
            </div>

            {/* Mobile Layout */}
            <div className="flex md:hidden w-full max-w-sm flex-col justify-center items-center">
              <div className="flex flex-col items-center">
                <p className="h-[80px] text-[4rem] sm:text-[5rem] font-bold text-white align-top leading-none">
                  ERROR
                </p>
                <p className="text-[1.25rem] sm:text-[1.5rem] h-fit font-bold p-0 text-white mt-6 text-center">
                  SOMETHING WENT WRONG
                </p>
              </div>
              <div className="w-full mt-8 px-2">
                <p className="text-[14px] text-white text-center leading-relaxed">
                  We're sorry, but something unexpected happened. Please try refreshing the page.
                </p>
              </div>
              <div className="w-full grid grid-cols-1 gap-3 mt-8 px-4">
                <Button
                  className="w-full text-white font-bold text-[1rem] bg-tdplplum h-12"
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </Button>
                <Button
                  className="w-full text-white font-bold text-[1rem] bg-tdpllightgrey h-12"
                  onClick={() => (window.location.href = window.location.origin)}
                >
                  Home Page
                </Button>
              </div>

              {/* Show error stack trace in development - Mobile */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-8 w-full px-4">
                  <summary className="text-sm text-white cursor-pointer hover:text-gray-300 text-center">
                    Show error stack trace
                  </summary>
                  <div className="mt-2 p-3 bg-black/50 rounded text-xs text-white overflow-auto max-h-[150px]">
                    <div className="font-bold mb-2">Error:</div>
                    <pre className="whitespace-pre-wrap break-all text-[10px]">
                      {this.state.error.stack || this.state.error.toString()}
                    </pre>
                    {this.state.errorInfo && (
                      <>
                        <div className="font-bold mt-4 mb-2">Component Stack:</div>
                        <pre className="whitespace-pre-wrap break-all text-[10px]">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </>
                    )}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
